import type { VoidFunctionComponent } from 'react';
import { useSelector } from 'react-redux';
import DropdownButton from '../../generic/DropdownButton';
import * as IssuesV2Store from '../../../redux/issuesV2';
import useFetchIssuesAction from './hooks/useFetchIssuesAction';
import type { AnySort } from '../../../../shared/issuesV2';
import {
  CREATED_AT_SORT,
  EPSS_SORT,
  ISSUE_COUNT_SORT,
  ISSUE_SORT_OPTION_BY_CATEGORY,
  PACKAGE_SORT,
  REVISION_SORT_OPTION_BY_CATEGORY,
  SEVERITY_SORT,
} from '../../../../shared/issuesV2';
import { Icon, icons } from '../../generic';
import { useIsFeatureEnabled } from '../../../helpers/features';
import { FULL_SECURITY } from '../../../../shared/featureFlags/flagNames';
import { window } from '../../../globals';
import { PREMIUM_COMP_SEC_PLAN_NAME } from '../../../../shared/subscriptions';
import ReadOnlySection from '../../generic/ReadOnlySection';
import { useSetFilterHasChanged } from '../../../redux/savedFilters/hooks';

const SORT_LABEL: Readonly<Record<AnySort, string>> = Object.freeze({
  [PACKAGE_SORT.ASC]: 'Package (A-Z)',
  [PACKAGE_SORT.DESC]: 'Package (Z-A)',
  [SEVERITY_SORT.ASC]: 'Lowest Severity',
  [SEVERITY_SORT.DESC]: 'Highest Severity',
  [EPSS_SORT.ASC]: 'Lowest EPSS',
  [EPSS_SORT.DESC]: 'Highest EPSS',
  [CREATED_AT_SORT.ASC]: 'Oldest',
  [CREATED_AT_SORT.DESC]: 'Newest',
  [ISSUE_COUNT_SORT.ASC]: 'Least issues',
  [ISSUE_COUNT_SORT.DESC]: 'Most issues',
});

const restrictedSecuritySorts = new Set<AnySort>([EPSS_SORT.ASC, EPSS_SORT.DESC]);

/**
 * IssuesSortingDropdown is a Redux-connected component
 * which houses the sorting options. Note that the sort options
 * which are displayed are dependent on the issueCategory.
 */
const IssuesSortingDropdown: VoidFunctionComponent = () => {
  const sort = useSelector(IssuesV2Store.selectors.getSort);
  const grouping = useSelector(IssuesV2Store.selectors.getGrouping);
  const issueCategory = useSelector(IssuesV2Store.selectors.getIssueCategory);
  const setSort = useFetchIssuesAction(IssuesV2Store.actions.setSort);
  const setHasChanged = useSetFilterHasChanged();
  const labels =
    grouping === 'issue'
      ? ISSUE_SORT_OPTION_BY_CATEGORY[issueCategory]
      : REVISION_SORT_OPTION_BY_CATEGORY[issueCategory];

  const isLimitedSecurity = !useIsFeatureEnabled(FULL_SECURITY);
  // We can't just use the PaidFeatureGate in this component due to the DropdownButton component.
  // If we wrap the contents of DropdownButton.Item, the onClick handling for PaidFeatureGate doesn't work because the main
  // DropdownButton.Item will handle the click event. If we wrap the whole DropdownButton.Item, then the styling gets messed up.
  // Because of this, we need to add logic to the onSelect handler for DropdownButton and manually apply some styling.
  return (
    <div className="issues-dropdown" data-test-id="issues-sorting-dropdown">
      <DropdownButton
        id="IssuesSortingDropdown"
        title={
          <span>
            <Icon icon={icons.sortDescending} /> {SORT_LABEL[sort]}
          </span>
        }
        onSelect={(selectedSort) => {
          if (isLimitedSecurity && restrictedSecuritySorts.has(selectedSort)) {
            window.showPlanSelectorModal({
              // We don't have to worry about showing Enterprise path here because no enterprise tiers have limited security.
              tier: PREMIUM_COMP_SEC_PLAN_NAME,
            });
            return;
          }

          setSort(selectedSort);
          setHasChanged(true);
        }}
        size="medium"
        pullRight
      >
        {labels.map((labelKey) => (
          <DropdownButton.Item key={labelKey} eventKey={labelKey} active={sort === labelKey}>
            <ReadOnlySection isReadOnly={isLimitedSecurity && restrictedSecuritySorts.has(labelKey)} isClickable>
              {SORT_LABEL[labelKey]}
            </ReadOnlySection>
            {isLimitedSecurity && restrictedSecuritySorts.has(labelKey) && <Icon icon={icons.lockOutline} />}
          </DropdownButton.Item>
        ))}
      </DropdownButton>
    </div>
  );
};

export default IssuesSortingDropdown;
